import apisauce, { ApiResponse } from 'apisauce'
import Moment from 'moment-timezone'
import Config from '../../Config'
import { Camelized, camelizeKeys } from 'humps'

import { resolveExtraProps } from 'APP/Lib/MessagingHelpers'
import type { NormalizedPostList } from 'APP/Lib/MessagingHelpers/types'
import { APP_SENDER } from 'APP/Lib/constants'
import type { ServerError } from '@mattermost/types/errors'
import type { UserProfile } from '@mattermost/types/users'
import { ChannelViewResponse } from '@mattermost/types/channels'
import { AxiosRequestConfig } from 'axios'
import { FileInfo, FileUploadResponse } from '@mattermost/types/files'

import type {
  MattermostChannel,
  MattermostChannelMembership,
  NewPost,
  RawPost,
  RawPostList,
} from './types'

const buildGetUserAvatarURL = (userId: string) => `/api/v4/users/${userId}/image`

// constants
const DEFAULT_POSTS_LIMIT = 60

class RequestError extends Error {
  constructor(name: string, message?: string) {
    super(message)
    if (Error.captureStackTrace) Error.captureStackTrace(this, RequestError)
    this.name = name
    this.message = message || name
  }
}

const create = (accessToken?: string | null, baseURL = Config.MATTERMOST_API_URL) => {
  const api = apisauce.create({
    baseURL,
    headers: {
      'Cache-Control': 'no-cache',
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
    },
    timeout: 15000,
  })

  const handleResponse = <T>(response: ApiResponse<T, ServerError>): NonNullable<T> => {
    if (response.ok) {
      if (response.data) {
        return response.data
      }
      // This case needs to be handled to satisfy the typechecker because apisauce always types the data as optional,
      // even when we explicitly pass a type parameter to ApiResponse. It never happens in real life if our type parameter
      // is accurate.
      // See https://github.com/infinitered/apisauce/issues/301
      throw new RequestError('Received unexpected empty response')
    } else {
      throw new RequestError(response.problem, response?.data?.message)
    }
  }

  const handleResponseWithExtraProps = (
    response: ApiResponse<RawPostList, ServerError>
  ): Promise<NormalizedPostList> => {
    const data = handleResponse(response)
    return resolveExtraProps(data, accessToken)
  }

  const buildQueryString = (parameters: Record<string, string | number>): string => {
    const keys = Object.keys(parameters)
    if (keys.length === 0) {
      return ''
    }

    let query = '?'
    for (let i = 0; i < keys.length; i++) {
      const key = keys[i]
      query += key + '=' + encodeURIComponent(parameters[key])

      if (i < keys.length - 1) {
        query += '&'
      }
    }

    return query
  }

  const getBaseRoute = (): string => {
    return 'api/v4'
  }

  const getUsersRoute = (): string => {
    return `${getBaseRoute()}/users`
  }

  const getChannelsRoute = (): string => {
    return `${getBaseRoute()}/channels`
  }

  const getChannelRoute = (channelId: string): string => {
    return `${getChannelsRoute()}/${channelId}`
  }

  const getUserRoute = (userId: string): string => {
    return `${getUsersRoute()}/${userId}`
  }

  const getFilesRoute = (): string => {
    return `${getBaseRoute()}/files`
  }

  const getPostsRoute = (): string => {
    return `${getBaseRoute()}/posts`
  }

  const getMe = (): Promise<UserProfile> => {
    return api.get<UserProfile, ServerError>(getUserRoute('me')).then(handleResponse)
  }

  const getPostsBeforePostId = (
    channelId: string,
    postId: string,
    limit = DEFAULT_POSTS_LIMIT
  ): Promise<NormalizedPostList> => {
    const url = `${getChannelRoute(channelId)}/posts${buildQueryString({
      before: postId,
      page: 0,
      per_page: limit,
    })}`

    return api
      .get<RawPostList, ServerError>(url)
      .then((response) => handleResponseWithExtraProps(response))
  }

  const getMyChannels = (teamId: string): Promise<MattermostChannel[]> => {
    const url = `${getUserRoute('me')}/teams/${teamId}/channels`
    return api.get<MattermostChannel[], ServerError>(url).then(handleResponse)
  }

  const getMyChannelsMembers = (teamId: string): Promise<MattermostChannelMembership[]> => {
    const url = `${getUserRoute('me')}/teams/${teamId}/channels/members`
    return api.get<MattermostChannelMembership[], ServerError>(url).then(handleResponse)
  }

  const getMyChannelMembers = (
    channelId: string
  ): Promise<Camelized<MattermostChannelMembership[]>> => {
    const url = `${getBaseRoute()}/channels/${channelId}/members`
    return api
      .get<MattermostChannelMembership[], ServerError>(url)
      .then(handleResponse)
      .then((data) => camelizeKeys(data))
  }

  const viewMyChannel = (channelId: string): Promise<ChannelViewResponse> => {
    const data = { channel_id: channelId }
    const url = `${getChannelsRoute()}/members/me/view`
    return api.post<ChannelViewResponse, ServerError>(url, data).then(handleResponse)
  }

  interface GetPostsOptions {
    limit?: number
  }

  const getPosts = (
    channelId: string,
    options: GetPostsOptions = {}
  ): Promise<NormalizedPostList> => {
    const limit = options.limit || DEFAULT_POSTS_LIMIT
    const url = `${getChannelRoute(channelId)}/posts${buildQueryString({
      page: 0,
      per_page: limit,
    })}`

    return api
      .get<RawPostList, ServerError>(url)
      .then((response) => handleResponseWithExtraProps(response))
  }

  const getPostsSince = (channelId: string, time: number): Promise<NormalizedPostList> => {
    const url = `${getChannelRoute(channelId)}/posts${buildQueryString({ since: time })}`
    return api.get<RawPostList, ServerError>(url).then((response) => {
      if (response.ok && response.data) {
        // For some reason this endpoint returns deleted posts, so we have to filter them
        const posts = response.data.posts
        const order = response.data.order
        let filteredPosts = Object.keys(posts).reduce<Record<string, RawPost>>((filtered, key) => {
          const currentPost = posts[key]
          if (currentPost.delete_at) {
            const postId = currentPost.id
            const index = order.indexOf(postId)
            if (index > -1) {
              order.splice(index, 1)
            }
          } else {
            filtered[key] = currentPost
          }
          return filtered
        }, {})

        return resolveExtraProps({ order, posts: filteredPosts }, accessToken)
      } else {
        throw new RequestError(response.problem || 'unknown')
      }
    })
  }

  const getUserPost = (
    channelId: string,
    text: string,
    file_ids: string[],
    deviceId: string,
    localId: number,
    answer: string | object | null,
    flags: Record<string, unknown>,
    payload: Record<string, unknown> | null | undefined,
    language: string,
    location?: string | null
  ): NewPost => {
    // TODO: update dialogue.messages to add the props passed here that aren't in the library, or remove these if unused
    const props: NewPost['props'] = {
      sender: APP_SENDER,
      ...flags,
      deviceId,
      localId,
      timezone: Moment.tz.guess(),
      language,
      ...(location && { location }),
    }

    if (answer) {
      if (typeof answer === 'string') {
        props.answer = answer
      } else {
        props.answer = JSON.stringify(answer)
      }
    }

    if (payload) {
      props.payload = payload
    }

    return {
      channel_id: channelId,
      message: text,
      props,
      file_ids,
    }
  }

  const createUserPost = (
    channelId: string,
    text: string,
    file_ids: string[],
    deviceId: string,
    localId: number,
    answer: string | object | null,
    flags: Record<string, unknown>,
    payload: Record<string, unknown> | null | undefined,
    language: string,
    location?: string | null
  ): Promise<ApiResponse<RawPost, ServerError>> => {
    const post = getUserPost(
      channelId,
      text,
      file_ids,
      deviceId,
      localId,
      answer,
      flags,
      payload,
      language,
      location
    )
    const url = `${getPostsRoute()}`

    return api.post<RawPost, ServerError>(url, post)
  }

  interface GetPublicUrlParams {
    fileId: string
  }

  const getPublicUrl = ({ fileId }: GetPublicUrlParams): Promise<string | undefined> => {
    const url = `${getFilesRoute()}/${fileId}/link`

    return api.get<FileInfo, ServerError>(url).then((response) => {
      if (response.ok) {
        return response.data?.link
      } else {
        throw new RequestError(response.problem)
      }
    })
  }

  interface GetPostFileInfoParams {
    postId: string
  }

  const getPostFileInfo = ({ postId }: GetPostFileInfoParams): Promise<FileInfo[]> => {
    const url = `${getPostsRoute()}/${postId}/files/info`
    return api.get<FileInfo[], ServerError>(url).then((response) => handleResponse(response))
  }

  type GetFileDataResponse = {
    uri?: string
    type?: string
    name: string
  }

  const getFileData = (file: FileObj): GetFileDataResponse => {
    let name =
      file.fileName ||
      (file.fileType?.startsWith('image') ? `${+new Date()}.jpg` : `${+new Date()}.pdf`)

    return {
      uri: file.fileUri,
      type: file.fileType,
      name,
    }
  }

  interface FileObj {
    file?: File
    fileUri?: string
    fileType?: string
    fileName?: string
  }

  const getFileFormData = (channelId: string, fileObj: FileObj): FormData => {
    const body = new FormData()
    // If we have a file of type `File`, we're on web and we can send that file object directly
    // Otherwise, we're on mobile and we need to create a file object from the uri
    const fileData = fileObj.file ? fileObj.file : getFileData(fileObj)
    body.append('files', fileData as File)
    body.append('channel_id', channelId)
    return body
  }

  const uploadFile = async (
    channelId: string,
    fileObj: FileObj,
    extraConfig: AxiosRequestConfig = {}
  ): Promise<FileUploadResponse> => {
    const url = `${getFilesRoute()}`
    const formData = getFileFormData(channelId, fileObj)
    return api
      .post<FileUploadResponse, ServerError>(url, formData, {
        headers: {
          // @ts-expect-error This is a valid header
          'Content-Type': 'multipart/form-data',
          ...extraConfig.headers,
        },
        timeout: extraConfig.timeout ?? 60000,
        ...(extraConfig as Record<string, string>),
      })
      .then(handleResponse)
  }

  return {
    buildQueryString,
    getBaseRoute,
    getUsersRoute,
    getChannelsRoute,
    getChannelRoute,
    getUserRoute,
    getFilesRoute,
    getPostsRoute,
    getMe,
    getMyChannels,
    getUserPost,
    getMyChannelsMembers,
    getMyChannelMembers,
    getFileData,
    getFileFormData,
    viewMyChannel,
    getPosts,
    getPostsBeforePostId,
    getPostsSince,
    createUserPost,
    uploadFile,
    getPublicUrl,
    getPostFileInfo,
  }
}

export default {
  create,
}

export type MattermostClient = ReturnType<typeof create>

export const buildAvatarUrl = (userId: string) => {
  return Config.MATTERMOST_API_URL + buildGetUserAvatarURL(userId)
}
